import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { DateField } from '../../../../components/inputs/DateField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { leaveCreditApi, leaveTypeApi } from '../../leave/leaveApi';
import {
  LEAVE_ENTRY_TYPE_LABELS,
  LeaveCreditKind,
  LeaveLedgerEntryType,
  type LeaveLedgerEntry,
  type LeaveLedgerEntryTypeValue,
  type LeaveCreditBalance,
  type LeaveType,
} from '../../leave/leaveTypes';
import { LeaveBalanceFigure } from '../../leave/components/LeaveBalanceFigure';
import { remainingBalance } from '../../leave/leaveBalance';

/*
  The entries a type can take — the server's refusals, offered as a choice rather than met as
  an error. A GRANTED type keeps no running balance, so Used and Adjustment cannot mean anything
  against one. A type SHARING its parent's credit takes Used alone: the credit is the parent's,
  so beginning balances, earnings and corrections are recorded there.
*/
function allowedEntries(type: LeaveType | null): LeaveLedgerEntryTypeValue[] {
  const all = Object.values(LeaveLedgerEntryType) as LeaveLedgerEntryTypeValue[];

  if (type?.sharesParentCredit && type.parentLeaveTypeId != null) {
    return [LeaveLedgerEntryType.Used];
  }

  return all.filter((value) =>
    type?.creditKind !== LeaveCreditKind.Grant
    || (value !== LeaveLedgerEntryType.Used && value !== LeaveLedgerEntryType.Adjustment));
}

/**
 * Records or corrects one credit-affecting event on an employee's leave.
 *
 * **Days are entered POSITIVE for everything but an adjustment**, and the server applies the
 * sign from the entry type. Asking a clerk to type `-3` for three days taken invites the row
 * that is wrong by six, and the sign is not a fact they know — it is a consequence of which
 * kind of entry they are making. An adjustment keeps its sign, because its direction is the
 * whole point of it.
 */
export function LeaveLedgerEntryModal({
  personnelId,
  entry,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null creates. */
  entry: LeaveLedgerEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [types, setTypes] = useState<LeaveType[]>([]);

  const [leaveTypeId, setLeaveTypeId] = useState(entry?.leaveTypeId ?? '');
  const [entryType, setEntryType] = useState<LeaveLedgerEntryTypeValue>(
    entry?.entryType ?? LeaveLedgerEntryType.Earned,
  );
  // Shown positive whatever the row holds; the sign is the server's to apply.
  const [days, setDays] = useState(entry ? String(Math.abs(entry.days)) : '');
  const [effectiveDate, setEffectiveDate] = useState(
    entry?.effectiveDate ?? new Date().toISOString().slice(0, 10),
  );
  const [reason, setReason] = useState(entry?.reason ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    The balances behind each type's trailing figure. A failed fetch (a writer without
    `leave-credits.read`) leaves them null and the options simply show none.
  */
  const [balances, setBalances] = useState<LeaveCreditBalance[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    leaveCreditApi
      .balances(personnelId, controller.signal)
      .then(setBalances)
      .catch(() => {
        /* No figures; the picker itself still works. */
      });
    return () => controller.abort();
  }, [personnelId]);

  useEffect(() => {
    const controller = new AbortController();
    leaveTypeApi
      .options(controller.signal)
      .then(setTypes)
      .catch(() => {
        /* The picker simply stays empty; the form reports its own failures on save. */
      });
    return () => controller.abort();
  }, []);

  /*
    The employee's live entries, to know which types already HAVE some: a beginning balance is
    recorded first or not at all (the server refuses it after any entry). A failed fetch leaves
    it null, and the option stays offered — the server's refusal then names the field.
  */
  const [ledger, setLedger] = useState<LeaveLedgerEntry[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    leaveCreditApi
      .ledger(personnelId, false, controller.signal)
      .then(setLedger)
      .catch(() => {
        /* Unknown; see above. */
      });
    return () => controller.abort();
  }, [personnelId]);

  // Any live entry on the type — or from a child SHARING its credit — other than this one.
  function hasEntries(typeId: string): boolean {
    const sharers = new Set(
      types.filter((t) => t.sharesParentCredit && t.parentLeaveTypeId === typeId).map((t) => t.id),
    );

    return (ledger ?? []).some((e) =>
      e.isActive
      && e.id !== entry?.id
      && (e.leaveTypeId === typeId || sharers.has(e.leaveTypeId)));
  }

  // The row that IS the type's beginning balance keeps that option: correcting it is not recording one.
  function offeredFor(type: LeaveType | null): LeaveLedgerEntryTypeValue[] {
    const allowed = allowedEntries(type);
    if (type == null) return allowed;

    const isItsBeginning =
      entry?.entryType === LeaveLedgerEntryType.BeginningBalance && entry.leaveTypeId === type.id;

    return isItsBeginning || !hasEntries(type.id)
      ? allowed
      : allowed.filter((value) => value !== LeaveLedgerEntryType.BeginningBalance);
  }

  const chosen = types.find((t) => t.id === leaveTypeId) ?? null;

  // Offering an entry the server refuses would be a control whose only outcome is a refusal.
  const entryOptions = offeredFor(chosen);

  const isAdjustment = entryType === LeaveLedgerEntryType.Adjustment;
  const parsedDays = Number(days);
  const daysValid = days.trim() !== '' && Number.isFinite(parsedDays) && parsedDays !== 0;

  const valid =
    leaveTypeId !== ''
    && daysValid
    && effectiveDate !== ''
    && (!isAdjustment || reason.trim().length > 0);

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      leaveTypeId,
      entryType,
      days: parsedDays,
      effectiveDate,
      reason: reason.trim() || null,
    };

    try {
      if (entry) await leaveCreditApi.update(personnelId, entry.id, request);
      else await leaveCreditApi.create(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this entry.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'leave-entry' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    // A type with its OWN ledger: a sharing child refuses the Earned row generated below.
    const ledgerType =
      types.find((t) => t.creditKind === LeaveCreditKind.Ledger && !t.sharesParentCredit) ?? types[0];
    if (!ledgerType) return;

    setLeaveTypeId(ledgerType.id);
    setEntryType(LeaveLedgerEntryType.Earned);
    setDays('1.25');
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    setReason('Recorded from the monthly accrual sheet.');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={entry ? 'Edit leave credit entry' : 'Record leave credit entry'}
      size="lg"
      id="leave-entry-modal"
      autogenerate={entry === null ? { onGenerate: generate, disabled: saving } : undefined}
      confirm={{
        id: 'leave-entry-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="leave-entry-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id="leave-entry-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          FULL ROWS for the two pickers (product call, 27 Oct 2026): the type option carries the
          employee's remaining balance at its outer right, which a half-width panel crowded
          against the name. Days and the date stay paired in the grid below — one row, no orphan.
        */}
        <Select
          id="leave-entry-leave-type-id"
          label="Leave type"
          required
          description="Which kind of leave this entry affects."
          value={leaveTypeId}
          onChange={(next) => {
            setLeaveTypeId(next);

            // Keep the Entry answerable: a kind the new type refuses snaps to one it takes.
            const offered = offeredFor(types.find((t) => t.id === next) ?? null);
            if (!offered.includes(entryType)) setEntryType(offered[0] ?? LeaveLedgerEntryType.Earned);
          }}
          error={leaveTypeId ? error?.fieldError('leaveTypeId') : 'Choose a leave type.'}
          disabled={saving}
          options={types.map((t) => ({
            value: t.id,
            label: t.name,
            sublabel: t.code ?? undefined,
            // What is left, at the outer right — a sharing child reads its parent's balance.
            right: <LeaveBalanceFigure value={remainingBalance(balances, t.id)} />,
          }))}
        />

        <Select
          id="leave-entry-entry-type"
          label="Entry"
          required
          description="What this row records — a beginning balance, credit earned, credit used, or a correction. A beginning balance comes first: once the type has any entry, record an adjustment instead."
          /* Numeric on the wire: String in, Number back out. */
          value={String(entryType)}
          onChange={(next) => setEntryType(Number(next) as LeaveLedgerEntryTypeValue)}
          error={error?.fieldError('entryType')}
          disabled={saving}
          options={entryOptions.map((value) => ({
            value: String(value),
            label: LEAVE_ENTRY_TYPE_LABELS[value],
          }))}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="leave-entry-days"
            label="Days"
            required
            description={
              isAdjustment
                ? 'Positive to add credit, negative to take it away.'
                : 'In days — halves and quarters are fine.'
            }
            value={days}
            onChange={(e) => setDays(e.target.value)}
            error={daysValid ? error?.fieldError('days') : 'Enter a number of days.'}
            disabled={saving}
          />

          <DateField
            id="leave-entry-effective-date"
            label="Effective date"
            required
            description="The day the credit was earned, taken or corrected — not today, unless they are the same."
            value={effectiveDate}
            onChange={setEffectiveDate}
            error={error?.fieldError('effectiveDate')}
            disabled={saving}
          />
        </div>

        {/* Full width, outside the grid as a sibling: a free note about the entry as a whole. */}
        <Textarea
          id="leave-entry-reason"
          label="Reason"
          required={isAdjustment}
          description="Why this entry exists. Required on a correction, where the reason is not implied by what it is."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={
            isAdjustment && !reason.trim()
              ? 'Say why this adjustment is being made.'
              : error?.fieldError('reason')
          }
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
